import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Eye, MousePointerClick, Globe, Activity } from "lucide-react";
import { TopViewedProducts } from "@/components/TopViewedProducts";
import { FunnelDiagnosisCard } from "@/components/FunnelDiagnosisCard";

const fmt = (n) => (n ?? 0).toLocaleString();

export function TrafficOverview() {
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/analytics/admin/traffic").then(({ data }) => setT(data)).catch(() => setErr(true));
  }, []);

  if (err) return null;
  if (!t) return <div className="mt-10 text-sm text-neutral-500" data-testid="traffic-loading">Loading traffic…</div>;

  return (
    <section className="mt-10" data-testid="traffic-overview">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Traffic &mdash; all time</div>
      <h2 className="mt-1 font-black text-xl tracking-tight">Total views &amp; clicks</h2>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Eye} testId="traffic-views-total" label="Product views" value={fmt(t.views_total)} sub={`${fmt(t.views_24h)} in last 24h`} accent="text-[#34C759]" />
        <Kpi icon={MousePointerClick} testId="traffic-clicks-total" label="Campaign clicks" value={fmt(t.clicks_total)} sub={`${fmt(t.clicks_7d)} last 7d &middot; ${fmt(t.bot_clicks_total)} bots filtered`} accent="text-[#007AFF]" />
        <Kpi icon={Globe} testId="traffic-pageviews-total" label="Site visits" value={fmt(t.pageviews_total)} sub="page views (visitors who accepted analytics)" />
        <Kpi icon={Activity} testId="traffic-visitors-total" label="Unique visitors" value={fmt(t.visitors_total)} sub={`${fmt(t.visitors_7d)} in last 7 days`} accent="text-[#FF9F0A]" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="traffic-chart">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Last 30 days</div>
        <h3 className="font-black text-xl tracking-tight mt-1">Daily views &amp; clicks</h3>
        <div className="mt-6 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={t.daily}>
              <CartesianGrid stroke="#27272A" vertical={false} />
              <XAxis dataKey="day" stroke="#71717a" fontSize={11} interval={4} tick={{ fontFamily: "monospace" }} />
              <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A" }} labelStyle={{ color: "#fff" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="views" name="Product views" stroke="#34C759" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="clicks" name="Campaign clicks" stroke="#007AFF" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="visits" name="Site visits" stroke="#FF9F0A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-3 text-xs text-neutral-500">
          Product views now count every visitor (logged in or not), once per visitor per product per day, with bots excluded.
          Daily click history starts from today; the all-time total includes earlier clicks.
        </p>
      </div>
      <TopViewedProducts items={t.top_products_7d || []} />
      </div>
      <FunnelDiagnosisCard />
    </section>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
        <Icon className="h-4 w-4 text-neutral-500" />
      </div>
      <div className={`mt-3 font-mono tabular-nums font-bold text-3xl ${accent || "text-white"}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1" dangerouslySetInnerHTML={{ __html: sub }} />}
    </div>
  );
}
